<template>
  <v-col class="route-guides-content">
    <v-row>
      <v-col>
        <docs-hero-banner
          eyebrow="Route Guides"
          title="Route-Specific Onboarding"
          description="Route-specific onboarding for the major areas of iAdOps. Use these sections when you need screen-level guidance instead of a broad feature overview."
          :meta-items="heroMetaItems"
        />
      </v-col>
    </v-row>

    <v-row
      id="dashboard"
      class="mb-4"
    >
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-2">
            Dashboard
          </h2>
          <p class="body-2 mb-0">
            Use Dashboard to orient quickly: welcome details, onboarding actions,
            and release highlights.
          </p>
        </v-card>
      </v-col>
    </v-row>

    <v-row
      id="projects"
      class="mb-4"
    >
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-2">
            Projects
          </h2>
          <p class="body-2 mb-0">
            Understand tab semantics, filter behavior, and project lifecycle
            state before making edits or status changes.
          </p>
        </v-card>
      </v-col>
    </v-row>

    <v-row
      id="search"
      class="mb-4"
    >
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-2">
            Search
          </h2>
          <p class="body-2 mb-0">
            Search sets selected context. Your selection can change downstream
            report content and available options.
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Time Guide -->
    <v-row
      id="time"
      class="mb-4"
    >
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3">
            <font-awesome-icon
              :icon="['fas', 'clock']"
              class="mr-2 primary--text"
            />
            Time
          </h2>

          <p class="body-2 mb-3">
            The Time view supports daily time entry, monthly review, billable tracking, and project-level aggregation across four tabs.
          </p>

          <!-- Tabs -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Tabs
          </h3>

          <v-simple-table
            dense
            class="mb-4"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Tab</th>
                  <th>Purpose</th>
                  <th>Layouts</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="font-weight-bold">
                    Time Entry
                  </td>
                  <td>Create and edit daily time entries for the current week.</td>
                  <td>Horizontal (Calendar), Vertical (Columns)</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Month-To-Date
                  </td>
                  <td>Review month-to-date totals across all entries.</td>
                  <td>Cards, Table</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Billable
                  </td>
                  <td>Track and verify billable hours before invoicing.</td>
                  <td>Cards, Table</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Projects
                  </td>
                  <td>See time aggregated per project for effort and budget tracking.</td>
                  <td>Cards, Table</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Time Types -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Time Types
          </h3>

          <v-simple-table
            dense
            class="mb-4"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Requires</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="font-weight-bold">
                    BILLABLE
                  </td>
                  <td>Client-billable work hours.</td>
                  <td>Client, Duration, Notes</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    NON-BILLABLE
                  </td>
                  <td>Internal or non-client time (meetings, admin, training).</td>
                  <td>Client, Duration</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    PROJECT
                  </td>
                  <td>Time allocated to a specific project.</td>
                  <td>Client, Duration, Project, Notes</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    ADX
                  </td>
                  <td>Ad exchange operations time.</td>
                  <td>Client, Duration</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    ADX-PG
                  </td>
                  <td>Ad exchange programmatic guaranteed time.</td>
                  <td>Client, Duration</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Quick Start -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Quick Start
          </h3>

          <ol class="body-2 pl-4 mb-3">
            <li class="mb-1">
              Go to the <strong>Time Entry</strong> tab to log hours.
            </li>
            <li class="mb-1">
              Click <strong>+ Add Entry</strong> or press <kbd>N</kbd> to open the entry form.
            </li>
            <li class="mb-1">
              Select a client, time type, and enter duration in HH:MM format.
            </li>
            <li class="mb-1">
              Notes are required for <strong>Billable</strong> and <strong>Project</strong> time types.
            </li>
            <li class="mb-1">
              Use <strong>MTD</strong> and <strong>Billable</strong> tabs to review totals.
            </li>
            <li>Switch between <strong>Card</strong> and <strong>Table</strong> views using the layout dropdown.</li>
          </ol>

          <!-- Keyboard Shortcuts -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Keyboard Shortcuts (Time Entry Tab)
          </h3>

          <v-simple-table
            dense
            class="mb-0"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><kbd>←</kbd> <kbd>→</kbd></td>
                  <td>Navigate to previous / next week</td>
                </tr>
                <tr>
                  <td><kbd>N</kbd></td>
                  <td>New entry for today</td>
                </tr>
                <tr>
                  <td><kbd>T</kbd></td>
                  <td>Jump to current week</td>
                </tr>
                <tr>
                  <td><kbd>Esc</kbd></td>
                  <td>Clear selection</td>
                </tr>
                <tr>
                  <td><kbd>?</kbd></td>
                  <td>Show keyboard shortcuts dialog</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Admin Guide (role-gated) -->
    <v-row
      v-if="showInternalContent"
      id="admin"
      class="mb-4"
    >
      <v-col>
        <v-card
          outlined
          class="pa-4"
        >
          <h2 class="subtitle-1 font-weight-bold mb-3">
            <font-awesome-icon
              :icon="['fas', 'shield-halved']"
              class="mr-2 primary--text"
            />
            Admin
          </h2>

          <p class="body-2 mb-3">
            Admin routes contain higher-impact actions. Use guarded workflows and
            confirmation steps for data edits and sync operations.
          </p>

          <!-- Admin Views -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Admin Views
          </h3>

          <v-simple-table
            dense
            class="mb-4"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th>View</th>
                  <th>Purpose</th>
                  <th>Impact Level</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="font-weight-bold">
                    User Management
                  </td>
                  <td>Add, edit, delete users. Manage permissions and access levels.</td>
                  <td>
                    <v-chip
                      x-small
                      color="error"
                      text-color="white"
                    >
                      High
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Project Management
                  </td>
                  <td>CRUD operations on projects. Bulk edits, status changes.</td>
                  <td>
                    <v-chip
                      x-small
                      color="warning"
                      text-color="white"
                    >
                      Medium
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Project Merge
                  </td>
                  <td>Detect and merge duplicate projects. Two-step confirmation with impact preview.</td>
                  <td>
                    <v-chip
                      x-small
                      color="error"
                      text-color="white"
                    >
                      High
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Security
                  </td>
                  <td>Auth provider management, domain configuration.</td>
                  <td>
                    <v-chip
                      x-small
                      color="error"
                      text-color="white"
                    >
                      High
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Enterprise
                  </td>
                  <td>Tenant settings, organization configuration.</td>
                  <td>
                    <v-chip
                      x-small
                      color="warning"
                      text-color="white"
                    >
                      Medium
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Safety Guidelines -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Safety Guidelines
          </h3>

          <v-alert
            type="warning"
            text
            dense
            class="mb-3"
          >
            <strong>Before destructive actions</strong> — All delete operations show a confirmation dialog with record details. Review carefully before confirming.
          </v-alert>

          <ul class="body-2 pl-4 mb-3">
            <li class="mb-1">
              <strong>Project Merge</strong>: Uses a two-step review process. The survivor record is kept; donors are merged into it. Use "Swap Survivor" to change which record is kept.
            </li>
            <li class="mb-1">
              <strong>User Permissions</strong>: Changes take effect immediately. Double-check permission levels before saving.
            </li>
            <li class="mb-1">
              <strong>Security Providers</strong>: Disabling a provider shows a confirmation dialog. Re-enabling may require users to re-authenticate.
            </li>
          </ul>

          <!-- Merge Terminology -->
          <h3 class="caption font-weight-bold text-uppercase mb-2">
            Merge Terminology
          </h3>

          <v-simple-table
            dense
            class="mb-0"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="font-weight-bold">
                    Survivor
                  </td>
                  <td>The project record that will be kept as the primary after merge.</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Donor
                  </td>
                  <td>Records merged into the survivor. Flagged as merged after the operation.</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Swap Survivor
                  </td>
                  <td>Switch which record is kept vs merged. Use when the second record has better data.</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Separate
                  </td>
                  <td>Mark records as distinct (not duplicates). No data is merged or deleted.</td>
                </tr>
                <tr>
                  <td class="font-weight-bold">
                    Backup
                  </td>
                  <td>Create a backup of all records in a group without merging or deleting.</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
export default {
  name: 'RouteGuidesContent',
  components: {
    DocsHeroBanner: () => import('@/docs/components/DocsHeroBanner.vue'),
  },
  props: {
    showInternalContent: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    heroMetaItems() {
      return [
        { label: 'Audience: All signed-in users', icon: ['fas', 'users'] },
        { label: 'Best for route-level help', icon: ['fas', 'map-signs'] },
        { label: this.showInternalContent ? 'Includes internal-only guides' : 'External-safe guide set', icon: ['fas', 'shield-halved'] },
      ]
    },
  },
}
</script>
